import kebabCase from 'lodash/kebabCase';

import { capitalizeFirstLetter } from '../utils';

export const getCssVarName = (key: string): string => {
  return `--deprecated-${kebabCase(key)}`;
};

/**
 * convert a css box shadow to a css filter drop shadow
 * relies on the shadows using rgb colors (different shadows are splitted by `), `)
 */
export const boxShadowToFilter = (boxShadow: string): string => {
  const shadows = boxShadow.split('), ');
  return shadows
    .map((shadow) => `drop-shadow(${shadow}${shadow.endsWith(')') ? '' : ')'})`)
    .join(' ');
};

type addPrefix<TKey, TPrefix extends string> = TKey extends string
  ? `${TPrefix}${Capitalize<TKey>}`
  : never;

type Prefixed<
  TObject extends Record<string, string>,
  TPrefix extends string,
> = {
  [key in addPrefix<keyof TObject, TPrefix>]: string;
};

export const prefixObjectKeys = <
  TObject extends Record<string, string>,
  TPrefix extends string,
>(
  object: TObject,
  prefix: string
): Prefixed<TObject, TPrefix> => {
  return Object.entries(object).reduce(
    (acc: Record<string, string>, [key, value]) => {
      acc[`${prefix}${capitalizeFirstLetter(key)}`] = value;
      return acc;
    },
    {}
  ) as Prefixed<TObject, TPrefix>;
};

export const getCssVarObject = <TObject extends Record<string, string>>(
  object: TObject
): Prefixed<TObject, 'var'> => {
  return Object.entries(object).reduce((acc: Record<string, string>, [key]) => {
    acc[`var${capitalizeFirstLetter(key)}`] = `var(${getCssVarName(key)})`;
    return acc;
  }, {}) as Prefixed<TObject, 'var'>;
};

export const getFilterShadowObject = <TObject extends Record<string, string>>(
  object: TObject
): Prefixed<TObject, 'filter'> => {
  return Object.entries(object).reduce(
    (acc: Record<string, string>, [key, value]) => {
      acc[`filter${capitalizeFirstLetter(key)}`] = boxShadowToFilter(value);
      return acc;
    },
    {}
  ) as Prefixed<TObject, 'filter'>;
};
